import { useState } from 'react';

export default function MembershipSignupForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    questions: '',
    membershipPlan: '',
    agreeToTerms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else if (type === 'textarea') {
      if (value.length <= 500) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreeToTerms) {
      alert('Please agree to the Terms and Conditions');
      return;
    }

    if (!formData.membershipPlan) {
      alert('Please select a membership plan');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('streetAddress', formData.streetAddress);
      formBody.append('addressLine2', formData.addressLine2);
      formBody.append('city', formData.city);
      formBody.append('state', formData.state);
      formBody.append('zipCode', formData.zipCode);
      formBody.append('questions', formData.questions);
      formBody.append('membershipPlan', formData.membershipPlan);
      formBody.append('agreeToTerms', formData.agreeToTerms ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d6l2bbp4a3uojp5fg5g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          streetAddress: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          questions: '',
          membershipPlan: '',
          agreeToTerms: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'What treatments are covered?',
      answer: 'Vitamin Drip Memberships: Choose from any of our amazing vitamin infusions (excluding NAD). Add-ons are additional. NAD Drip Memberships: Each treatment includes 500mg or 1000mg of NAD plus the vitamin boost for maximum effectiveness and comfort.'
    },
    {
      question: 'Do treatments roll over if they are not used?',
      answer: 'Unused treatments do not roll over to the next month. We encourage you to use your monthly allotment to maintain optimal wellness.'
    },
    {
      question: 'Can I share my IV treatments with others?',
      answer: 'Memberships are non-transferable and for individual use only. However, members receive exclusive guest passes depending on their tier level.'
    }
  ];

  return (
    <section className="py-24 px-6 lg:px-12 bg-bone">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal mb-6 tracking-wide">
            Membership Signup
          </h2>
          <p className="text-greige text-lg font-light leading-relaxed">
            Join IV Hydration Club and start your wellness journey today
          </p>
        </div>

        <form 
          id="membership-signup-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
        >
          {/* Name Fields */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-4">
              Name <span className="text-sage">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First Name"
                  required
                  className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
                />
                <p className="text-xs text-greige mt-1 font-light">First</p>
              </div>
              <div>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  required
                  className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
                />
                <p className="text-xs text-greige mt-1 font-light">Last</p>
              </div>
            </div>
          </div>

          {/* Phone */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-2">
              Phone <span className="text-sage">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
            />
          </div>

          {/* Email */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-2">
              Email <span className="text-sage">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
            />
          </div>

          {/* Address */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-4">
              Address <span className="text-sage">*</span>
            </label>
            <div className="space-y-4">
              <input
                type="text"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
                placeholder="Street Address"
                required
                className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
              />
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Address Line 2"
                className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  required
                  className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
                />
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  required
                  className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
                />
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  placeholder="ZIP Code"
                  required
                  className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors cursor-text"
                />
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-2">
              Do you have any questions?
            </label>
            <textarea
              name="questions"
              value={formData.questions}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-greige/30 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors resize-none cursor-text"
            ></textarea>
            <p className="text-xs text-greige mt-1 font-light text-right">
              {formData.questions.length}/500 characters
            </p>
          </div>

          {/* Membership Plan Selection */}
          <div className="mb-8">
            <label className="block text-charcoal text-sm font-light mb-4">
              Please select your Monthly Membership Plan type below <span className="text-sage">*</span>
            </label>
            <div className="space-y-3">
              <p className="text-xs font-light text-greige mb-2">Vitamin Drip Membership Options</p>
              {[
                { value: 'Basic - $299 / Month', label: 'Basic - $299 / Month' },
                { value: 'Restore - $549/Month', label: 'Restore - $549/Month' },
                { value: 'Executive - $1000/Month', label: 'Executive - $1000/Month' },
                { value: 'Elite - $1750/ Month', label: 'Elite - $1750/ Month' },
              ].map((plan) => (
                <label key={plan.value} className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="membershipPlan"
                    value={plan.value}
                    checked={formData.membershipPlan === plan.value}
                    onChange={handleChange}
                    className="w-4 h-4 text-gold border-greige/30 focus:ring-gold cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-charcoal font-light group-hover:text-gold transition-colors">
                    {plan.label}
                  </span>
                </label>
              ))}
              
              <p className="text-xs font-light text-greige mt-4 mb-2">NAD Drip Membership Options</p>
              {[
                { value: 'NAD 1.0 $599/Month', label: 'NAD 1.0 $599/Month' },
                { value: 'NAD 2.0 $1,150/Month', label: 'NAD 2.0 $1,150/Month' },
                { value: 'NAD 4.0 $2,199 /Month', label: 'NAD 4.0 $2,199 /Month' },
                { value: 'NAD 8.0 $4,299 /Month', label: 'NAD 8.0 $4,299 /Month' },
              ].map((plan) => (
                <label key={plan.value} className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="membershipPlan"
                    value={plan.value}
                    checked={formData.membershipPlan === plan.value}
                    onChange={handleChange}
                    className="w-4 h-4 text-gold border-greige/30 focus:ring-gold cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-charcoal font-light group-hover:text-gold transition-colors">
                    {plan.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="mb-8">
            <label className="flex items-start cursor-pointer group">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                required
                className="w-4 h-4 mt-1 text-gold border-greige/30 rounded focus:ring-gold cursor-pointer"
              />
              <span className="ml-3 text-sm text-charcoal font-light group-hover:text-gold transition-colors">
                I agree to the Terms and Conditions <span className="text-gold">*</span>
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-charcoal text-white py-4 rounded-full text-sm font-light tracking-wide hover:bg-charcoal/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Membership Application'}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-gold/10 border border-gold/30 rounded-lg">
              <p className="text-gold text-sm font-light text-center">
                Thank you! Your membership application has been submitted successfully. We'll contact you shortly.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-light text-center">
                There was an error submitting your application. Please try again.
              </p>
            </div>
          )}
        </form>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-3xl font-light text-charcoal mb-8 text-center tracking-wide">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-sand/30 transition-colors cursor-pointer"
                >
                  <span className="text-charcoal font-light text-base">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-xl text-gold transition-transform duration-300 ${expandedFAQ === index ? 'rotate-180' : ''}`}></i>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-5">
                    <p className="text-greige text-sm font-light leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-white rounded-xl p-8 border border-greige/20">
          <h4 className="text-xl font-light text-charcoal mb-4 tracking-wide">Disclaimer</h4>
          <p className="text-greige text-xs font-light leading-relaxed">
            I acknowledge that I have carefully read this agreement, were afforded sufficient opportunity to consult with legal counsel of my choice and to ask questions and receive satisfactory answers regarding this agreement, understand my respective rights and obligations under it, and I have signed it of my free will and volition. I understand that this authorization will remain in effect until I cancel it in writing, and I agree to notify IV Hydration Club in writing of any changes in my account information or termination of this authorization at least 30 days prior to the next billing date after the 3-month minimum term is up. If the above noted payment dates fall on a weekend or holiday, I understand that the payments may be executed on the next business day. I acknowledge that the origination of Credit Card transactions to my account must comply with the provisions of U.S. law. I certify that I am an authorized user of this Credit Card and will not dispute these scheduled transactions; so long as the transactions correspond to the terms indicated in this authorization form.
          </p>
        </div>
      </div>
    </section>
  );
}